import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { UploadCloud } from "lucide-react";
import { Icon } from "@cntrl/ui";
import styles from "./UploadField.module.scss";

// The dropzone + determinate progress field (Upload row) reused by the asset editor (cover / photo /
// canvas) and the Songs master upload. Drag-over paints the accent border + tint; a picked image previews from
// a local object URL (no round-trip); the progress bar is fed by the caller's XHR upload. Purely
// presentational — the caller owns the actual upload + persistence, so a failure surfaces there.

export interface UploadFieldProps {
  /** Accept filter, e.g. "image/*", "video/*", "audio/*". */
  accept: string;
  file: File | null;
  onFile: (file: File | null) => void;
  /** Upload progress in [0,1] while a save is in flight; undefined = not uploading. */
  progress?: number;
  /** Existing content preview in edit mode when no new file is picked (e.g. a <MediaImage/> of the current media). */
  existing?: ReactNode;
  disabled?: boolean;
  /** The empty-state prompt (e.g. "Drop cover art or browse — JPG/PNG, 3000×3000"). */
  prompt: string;
  /**
   * `default` is the tall zone sized for release cover art, where the dropzone is the subject of the screen.
   * `compact` is roughly half the height with the prompt laid out in a ROW — for a form where the upload is
   * one field among many and the tall box crowds everything under it (the ad creator's Creative block).
   * Presentation only: every state and behaviour below is shared, which is why this is a density rather than
   * a second component.
   */
  density?: "default" | "compact";
}

export function UploadField({ accept, file, onFile, progress, existing, disabled, prompt, density = "default" }: UploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragover, setDragover] = useState(false);

  // Local preview for a freshly-picked image — no upload needed to show it.
  const previewUrl = useMemo(() => (file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const uploading = progress !== undefined;

  function pick(files: FileList | null) {
    const f = files && files.length > 0 ? files[0] : null;
    if (f) onFile(f);
  }

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.zone}
        // A stable accessible name independent of the visible content: once an image preview / existing media
        // (both decorative, alt="") fills the button, its children carry no text, so name it explicitly.
        aria-label={file || existing ? "Replace file" : "Choose a file to upload"}
        data-dragover={dragover || undefined}
        data-has-content={Boolean(file || existing) || undefined}
        data-density={density === "compact" ? "compact" : undefined}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragover(true);
        }}
        onDragLeave={() => setDragover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragover(false);
          if (!disabled) pick(e.dataTransfer.files);
        }}
      >
        {previewUrl ? (
          <img className={styles.preview} src={previewUrl} alt="" />
        ) : file ? (
          <span className={styles.fileName}>{file.name}</span>
        ) : existing ? (
          <span className={styles.existing}>{existing}</span>
        ) : (
          <span className={styles.prompt}>
            <Icon icon={UploadCloud} size={22} />
            {prompt}
          </span>
        )}
      </button>

      {/* The visible button drives this input (inputRef.click), so keep it off the tab order + out of the AT
          tree — otherwise it is a second, unlabeled "Choose File" stop (and an extra Modal focus-trap node). */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className={styles.input}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => pick(e.target.files)}
      />

      {uploading && (
        <div className={styles.progress} role="progressbar" aria-label="Upload progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((progress ?? 0) * 100)}>
          <span className={styles.progressFill} style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
        </div>
      )}
    </div>
  );
}
